// Article domain types for the frontend.
//
// The canonical contract (enums, IArticle) lives in `@atlas/types`. This module
// re-exports the enums and defines the FRONTEND VIEW shape, pinned to the real
// hydrated article payload — `{ ...article.toDTO(), classification }`
// (`articlePayload()` in article-controller.helpers.ts) — so the service needs no
// reshaping.

import type { AxisSelection, IArticleTranslationVariant, ObjectTag } from '@atlas/types'
import { ArticleStatus, ArticleTranslationStatus, ArticleType } from '@atlas/types'

export { ArticleStatus, ArticleTranslationStatus, ArticleType }

/**
 * One locale variant as `GET /articles/{id}/translations` returns it.
 *
 * Re-exported rather than mirrored: the canonical interface is already
 * wire-shaped (`publishedAt` and `updatedAt` are ISO strings), so a hand-written
 * copy could only drift from it. It carries no `content` — the coverage read
 * drops bodies, and a variant's body comes from `GET /articles/{id}`.
 */
export type ArticleTranslationVariant = IArticleTranslationVariant

/** An article's resolved classification, as the API hydrates it. */
export interface ArticleClassification {
	selection: AxisSelection
	tags: ObjectTag[]
}

/** An article as returned by the API (dates are ISO strings). */
export interface Article {
	articleId: string
	tenantId: number
	title: string
	slug: string
	summary: string | null
	/** Sanitized-on-render HTML. Never inject without the article sanitizer. */
	content: string
	articleType: ArticleType
	/** The file ids this article owns, in the author's order. */
	imageFileIds: string[]
	status: ArticleStatus
	publishedAt: string | null
	authorUserId: string | null
	languageIso: string
	translationGroupId: string
	isSource: boolean
	translationStatus: ArticleTranslationStatus | null
	translationFailureReason: string | null
	isActive: boolean
	createdBy: string | null
	createdAt: string
	updatedAt: string
	deletedAt: string | null
	classification: ArticleClassification
}

export interface CreateArticleInput {
	title: string
	slug?: string
	summary?: string | null
	content?: string
	articleType?: ArticleType
	imageFileIds?: string[]
	languageIso: string
	classification?: AxisSelection
}

export type UpdateArticleInput = Partial<CreateArticleInput>

/**
 * `GET /articles` query shape. `status` and `articleType` go out as
 * comma-separated `filters[…]` parameters (never Axios's default array
 * serialization, which would emit `filters[status][]=…`); each `classification`
 * axis becomes its own `filters[<axisCode>]` parameter; `search` and
 * `languageIso` ride the legacy JSON `filter` param the shared parser reads.
 */
export interface ArticleListParams {
	search?: string
	languageIso?: string
	status?: readonly ArticleStatus[]
	articleType?: readonly ArticleType[]
	classification?: AxisSelection
	page?: number
	limit?: number
	sort?: 'createdAt' | 'updatedAt' | 'publishedAt' | 'title'
	order?: 'asc' | 'desc'
}

/**
 * Mirrors the backend's `QUERY_CONFIG.DEFAULT_LIMIT` in
 * `article-controller.helpers.ts`. Pinned, never discovered from a response.
 */
export const ARTICLE_LIST_PAGE_SIZE = 20

/**
 * Mirrors `articleCreateSchema`'s verified `imageFileIds` cap
 * (`article.schema.ts`), so an over-long list is refused client-side rather than
 * 422'd after an upload has already happened.
 */
export const ARTICLE_IMAGE_LIMIT = 50

/** Human label for a type, for filter chips and the composer's control. */
export const ARTICLE_TYPE_LABELS: Readonly<Record<ArticleType, string>> = {
	[ArticleType.GENERAL]: 'General',
	[ArticleType.BRAND_DETAILS]: 'Brand details',
}

/**
 * Mirrors `MAX_LOCALES_PER_ARTICLE` in `packages/core/models/src/article/article.schema.ts`,
 * so an over-long selection is refused in the picker rather than 422'd after the
 * user has chosen twenty-one locales.
 */
export const ARTICLE_TRANSLATE_LOCALES_LIMIT = 20

/**
 * Mirrors `MAX_TRANSLATABLE_CONTENT_CHARS` in
 * `packages/functions/article-service/src/services/article-translation-service.ts`.
 * Pinned rather than imported: that is a Lambda package, and the frontend bundle
 * must not reach into one. Deliberately far below the 200,000-character storage
 * cap — the column is bounded, the model call is bounded differently.
 */
export const ARTICLE_TRANSLATABLE_CONTENT_LIMIT = 60_000

/** Human label for a status, for badges. */
export const ARTICLE_STATUS_LABELS: Readonly<Record<ArticleStatus, string>> = {
	[ArticleStatus.DRAFT]: 'Draft',
	[ArticleStatus.IN_REVIEW]: 'In review',
	[ArticleStatus.PUBLISHED]: 'Published',
	[ArticleStatus.ARCHIVED]: 'Archived',
}

/**
 * Human label for a translation job's state. Orthogonal to `ArticleStatus`: a
 * variant can be a published article whose last translation failed.
 */
export const ARTICLE_TRANSLATION_STATUS_LABELS: Readonly<Record<ArticleTranslationStatus, string>> = {
	[ArticleTranslationStatus.PENDING]: 'Queued',
	[ArticleTranslationStatus.TRANSLATING]: 'Translating…',
	[ArticleTranslationStatus.READY]: 'Translated',
	[ArticleTranslationStatus.FAILED]: 'Failed',
}
